import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '../types';
import { authApi } from '../api';
import { broadcastAuthChange } from '../authSync';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isLoggingOut: boolean; // Flag to prevent logout loops
  
  // Actions
  setUser: (user: User | null) => void;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  logout: (skipBroadcast?: boolean) => Promise<void>; // Allow skipping broadcast to prevent loops
  checkAuth: (options?: { silent?: boolean }) => Promise<void>;
  refresh: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: true,
      isAuthenticated: false,
      isLoggingOut: false,

      setUser: (user) => set({ 
        user,
        isAuthenticated: !!user,
        isLoading: false,
      }),

      login: async (email, password) => {
        try {
          set({ isLoading: true });
          const response = await authApi.login({ email, password });
          
          if (response.success && response.data?.user) {
            set({ 
              user: response.data.user, 
              isAuthenticated: true,
              isLoading: false,
            });
            // Broadcast login to sync with landing page
            broadcastAuthChange('login');
          } else {
            throw new Error('Login failed');
          }
        } catch (error) {
          set({ 
            isLoading: false,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      register: async (email, password, firstName, lastName) => {
        try {
          set({ isLoading: true });
          const response = await authApi.register({ 
            email, 
            password, 
            firstName, 
            lastName 
          });
          
          if (response.success) {
            set({ isLoading: false });
          } else {
            throw new Error('Registration failed');
          }
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async (skipBroadcast = false) => {
        // Prevent logout loops
        if (get().isLoggingOut) {
          console.warn('⚠️ Logout already in progress, skipping...');
          return;
        }

        set({ isLoggingOut: true });

        try {
          // Only call API if user is authenticated (to avoid unnecessary calls)
          if (get().isAuthenticated || get().user) {
            try {
              await authApi.logout();
            } catch (error: any) {
              // Ignore 401 errors (user already logged out) and other errors
              if (error?.status !== 401) {
                console.error('Logout error:', error);
              }
            }
          }
        } finally {
          set({ 
            user: null, 
            isAuthenticated: false,
            isLoading: false,
            isLoggingOut: false,
          });

          // Only broadcast if not skipped (to prevent loops when called from listener)
          if (!skipBroadcast) {
            broadcastAuthChange('logout');
          }
        }
      },

      checkAuth: async (options?: { silent?: boolean }) => {
        const silent = options?.silent === true;

        // Safety check: Don't check auth if we're on a public page
        // This prevents auth checks during initialization or before React Router updates location
        if (typeof window !== 'undefined') {
          const pathname = window.location.pathname;
          const urlParams = new URLSearchParams(window.location.search);
          const apiKey = urlParams.get('apiKey');
          
          if (pathname === '/guest-access' || pathname === '/guest-access/' || 
              pathname.startsWith('/partner/') ||
              (apiKey && (apiKey.startsWith('guest_') || apiKey === 'guest'))) {
            console.log('🔒 checkAuth() blocked - public page detected:', pathname);
            set({ 
              user: null, 
              isAuthenticated: false,
              isLoading: false,
            });
            return;
          }
        }

        try {
          // Only show global loading when not silent (e.g. initial load). On visibility change we don't want to unmount the UI.
          if (!silent) set({ isLoading: true });
          
          // Check if there's a user saved in localStorage before making API call
          // Zustand persist saves to localStorage with key 'auth-storage'
          let hasStoredUser = false;
          try {
            const stored = localStorage.getItem('auth-storage');
            if (stored) {
              const parsed = JSON.parse(stored);
              if (parsed?.state?.user) {
                hasStoredUser = true;
              }
            }
          } catch (e) {
            // localStorage might not be available or parse error
            console.warn('Could not check localStorage:', e);
          }
          
          // If no user in localStorage, skip API call and set loading to false
          if (!hasStoredUser) {
            set({ 
              user: null, 
              isAuthenticated: false,
              isLoading: false,
            });
            return;
          }
          
          // User exists in localStorage, verify with API that token is still valid
          // Add timeout to prevent infinite loading
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Auth check timeout')), 5000)
          );
          
          const response = await Promise.race([
            authApi.me(),
            timeoutPromise
          ]) as any;
          
          if (response?.success && response?.data?.user) {
            set({ 
              user: response.data.user, 
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            // Clear user and auth state
            set({ 
              user: null, 
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } catch (error: any) {
          // Only log non-401 errors (401 is expected when not authenticated)
          if (error?.status !== 401 && error?.message !== 'Auth check timeout') {
            console.error('Auth check failed:', error);
          }
          
          // If 401 (Unauthorized), remove token from localStorage
          if (error?.status === 401 || error?.message?.includes('Unauthorized')) {
            try {
              // Clear Zustand persisted state
              localStorage.removeItem('auth-storage');
            } catch (e) {
              // localStorage might not be available
              console.warn('Could not clear localStorage:', e);
            }
          }
          
          // On error, clear auth state - user is NOT authenticated
          set({ 
            user: null, 
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      refresh: async () => {
        try {
          await authApi.refresh();
          await get().checkAuth();
        } catch (error) {
          console.error('Refresh error:', error);
          set({ 
            user: null, 
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user,
        // Don't persist isAuthenticated - it should be calculated from user
      }),
    }
  )
);
